import { sponsorship } from "@/lib/lecturehub/sponsor";
import type { SponsoredTeaser } from "./events";

/**
 * A sponsored card in a list, for the sponsor's impressions and clicks
 * (GA's `view_promotion` / `select_promotion`, lib/analytics/events.ts).
 *
 * Every sponsored teaser already shows the sponsor chip, whatever the card
 * layout — so the chip carries these data attributes, and ONE observer
 * (components/analytics/SponsoredTeasers.tsx) counts an impression when the
 * chip is at least half visible for a second (the IAB viewability rule) and a
 * click when that card's link to the item is followed. A render site passes
 * `teaserAttrs(sponsoredTeaser(…))` to its chip and nothing else; the article
 * hero's own chip passes none — it is not a teaser.
 */
export type TeaserRef = {
  id: string;
  title: string;
  href: string;
  kind: "sponsored" | "partner";
  sponsor: string | null;
};

export function sponsoredTeaser(
  item: Parameters<typeof sponsorship>[0],
  card: { id: string; title: string | null | undefined; href: string },
): TeaserRef | null {
  const s = sponsorship(item);
  return s
    ? { id: card.id, title: card.title ?? "", href: card.href, kind: s.kind, sponsor: s.name }
    : null;
}

export function teaserAttrs(t: TeaserRef | null | undefined): Record<string, string> {
  if (!t) return {};
  return {
    "data-sponsored-teaser": t.id,
    "data-teaser-title": t.title,
    "data-teaser-href": t.href,
    "data-teaser-kind": t.kind,
    ...(t.sponsor ? { "data-teaser-sponsor": t.sponsor } : {}),
  };
}

/** The teaser an element's data attributes describe; null when incomplete. */
export function teaserFromDataset(d: DOMStringMap): TeaserRef | null {
  const kind =
    d.teaserKind === "partner" ? "partner" : d.teaserKind === "sponsored" ? "sponsored" : null;
  if (!d.sponsoredTeaser || !d.teaserHref || !kind) return null;
  return {
    id: d.sponsoredTeaser,
    title: d.teaserTitle ?? "",
    href: d.teaserHref,
    kind,
    sponsor: d.teaserSponsor ?? null,
  };
}

/** GA's promotion fields; the slot is the page the card was seen on. */
export function promotionParams(t: TeaserRef, slot: string): SponsoredTeaser {
  return {
    promotion_id: t.id,
    promotion_name: t.title.slice(0, 100),
    creative_name: t.kind,
    creative_slot: slot,
    sponsor_name: t.sponsor ?? undefined,
  };
}
